<template>
  <NuxtLayout :name="layoutName">
    <ErrorBoundary module-name="Article">
      <div class="article-page">
        <component
          :is="component"
          :id="`${article?.id}`"
          class="post article-page__component"
        />
        <NotificationBadge
          :icon="notificationBadge.icon"
          :show="isShowNotificationBadge"
          :text="notificationBadge.text"
          @close="resetNotificationBadge"
        />
      </div>
    </ErrorBoundary>
  </NuxtLayout>
</template>

<script lang="ts">
  import { throttle } from 'throttle-debounce';
  import MissPrint from '~/components/MissPrint.vue';
  import {
    getContentObject,
    type InstanceOptions,
    percentageInit,
    viewedContentPercent,
  } from '~/utils/percentagePage';
  import { MISSPRINT_SELECTION_MAX_LENGTH, YA_METRIC_ID } from '~/constants';
  import NotificationBadge from '~/components/NotificationBadge.vue';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { useSeoStore } from '~/store/seo';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import errorPath from '@/assets/images/svg/error.svg?raw';
  import ErrorBoundary from '@devhacker/shared/components/ErrorBoundary.vue';

  const DefaultArticle = defineAsyncComponent(
    () => import('~/components/templates/DefaultArticle.vue'),
  );
  const QuizArticle = defineAsyncComponent(() => import('~/components/templates/QuizArticle.vue'));
  const PromoArticle = defineAsyncComponent(
    () => import('~/components/templates/PromoArticle/PromoArticle.vue'),
  );
  const TableOfContentsTemplate = defineAsyncComponent(
    () => import('~/components/templates/TableOfContents.vue'),
  );
  const CardsTemplate = defineAsyncComponent(
    () => import('~/components/templates/Cards/Cards.vue'),
  );

  const scrolledElementSelector = '.default-article__analytics';
  const scrolledElementSelectorPromo = '.promo-article__analytics';

  const MISSPRINT_LENGTH_NOTIFICATION_BADGE = {
    text: 'Пожалуйста, выделите конкретное слово или предложение с ошибкой',
    icon: errorPath,
  };

  export default defineNuxtComponent({
    name: 'ArticlePage',

    components: {
      MissPrint,
      NotificationBadge,
      ErrorBoundary,
    },

    // для отправки аналитики
    // ArticleCardSmall -> CardFooter
    provide() {
      return {
        eventCategory: () => 'Внутренняя',
        eventActionCategory: () => 'Рубрика',
      };
    },

    setup() {
      // нет возможности менять динамически через эту штуку
      // поэтому мы отключаем глобальный и используем локальный тут
      definePageMeta({
        layout: false,
        middleware: ['article-data', 'preview-data'],
      });

      const article = useSingleArticleStore().article;
      const seo = useSeoStore().currentSeo;

      const layoutName = computed(() => (article?.is_promo ? 'promo' : 'default'));

      if (article && seo) {
        const {
          title,
          description,
          canonical,
          schema,
          schema_faq,
          schema_recipe,
          og_image,
          site_icon,
          robots,
        } = seo;

        const { last_modified, image } = article;
        const ldJsonContent = schema?.replace(/<script.*?>(.*)<\/script>/, '$1');

        // https://app.clickup.com/t/2xqpzwv
        const excludesYandexRecommendations = image?.sizes.length
          ? null
          : {
              key: 'yandex_recommendations_kill',
              property: 'yandex_recommendations_kill',
              content: '1',
            };

        useHead({
          title,

          meta: [
            { key: 'og:title', property: 'og:title', content: title },
            {
              key: 'og:image',
              property: 'og:image',
              content: og_image || site_icon,
            },
            {
              key: 'og:description',
              property: 'og:description',
              content: description,
            },
            { key: 'og:url', property: 'og:url', content: canonical },
            { key: 'description', name: 'description', content: description },
            {
              key: 'robots',
              name: 'robots',
              content: robots,
            },
            // twiiter
            { key: 'twitter:title', property: 'twitter:title', content: title },
            {
              key: 'twitter:description',
              property: 'twitter:description',
              content: description,
            },
            {
              key: 'twitter:card',
              property: 'twitter:card',
              content: 'summary_large_image',
            },
            { key: 'twitter:image', property: 'twitter:image', content: og_image },
            ...(excludesYandexRecommendations ? [excludesYandexRecommendations] : []),
            {
              key: 'last-modified',
              property: 'last-modified',
              content: last_modified,
            },
          ],

          script: [
            {
              type: 'application/ld+json',
              innerHTML: ldJsonContent,
            },
            ...(schema_faq
              ? [
                  {
                    type: 'application/ld+json',
                    innerHTML: schema_faq,
                  },
                ]
              : []),
            ...(schema_recipe
              ? [
                  {
                    type: 'application/ld+json',
                    innerHTML: schema_recipe,
                  },
                ]
              : []),
          ],

          link: [
            { key: 'canonical', rel: 'canonical', href: canonical },
            { key: 'amphtml', rel: 'amphtml', href: `${canonical}amp/` },
          ],
        });
      }

      return {
        layoutName,
        article,
        seo,
      };
    },

    data() {
      return {
        notificationBadge: {
          text: '',
          icon: '',
        },

        isShowNotificationBadge: false,
      };
    },

    computed: {
      throttledScroll() {
        return throttle(100, this.onScroll);
      },

      component() {
        // TODO: переименовать компоненты соответственно
        // типов шаблонов (QuizArticle -> ArticleBroadsideTemplate и т.д.)
        // соответственно внутри тоже переименовать классы и все сопутствующее

        if (this.article?.template === 'broadside') return QuizArticle;
        if (this.article?.template === 'promo') return PromoArticle;
        if (this.article?.template === 'cards') return CardsTemplate;
        if (this.article?.template === 'toc') return TableOfContentsTemplate;

        return DefaultArticle;
      },
    },

    mounted() {
      window.addEventListener('keydown', this.openModalMissPrint);

      percentageInit(scrolledElementSelector);
      percentageInit(scrolledElementSelectorPromo);

      document.addEventListener('click', this.handleArticleClick);

      window.addEventListener('scroll', this.throttledScroll);
    },

    beforeUnmount() {
      window.removeEventListener('scroll', this.throttledScroll);
    },

    unmounted() {
      window.removeEventListener('keydown', this.openModalMissPrint);
      document.removeEventListener('click', this.handleArticleClick);
    },

    methods: {
      handleArticleClick(e: MouseEvent): void {
        // Рекламный блок яндекса приходит в двух обертках, убеждаемся что верхняя с id adfox
        const targetGrandParent = (e.target as HTMLElement | null)?.parentElement?.parentElement;

        if (targetGrandParent && targetGrandParent.id.includes('adfox')) {
          useNuxtApp().$sendYandexMetrika({
            level1: 'Клик_РСЯ',
          });
        }
      },

      resetNotificationBadge(): void {
        this.notificationBadge = { text: '', icon: '' };
        this.isShowNotificationBadge = false;
      },

      openModalMissPrint(event: KeyboardEvent): void {
        if (event.ctrlKey && event.code === 'Enter') {
          const selection = window.getSelection() as Selection;
          const selectionText = selection?.toString();
          const selectionLink = window.location.href;

          if (selectionText.length > MISSPRINT_SELECTION_MAX_LENGTH) {
            this.notificationBadge = MISSPRINT_LENGTH_NOTIFICATION_BADGE;
            this.isShowNotificationBadge = true;
            return;
          }

          if (!selectionText) {
            return;
          }

          const selectionRange = selection.getRangeAt(0);
          const parentStart = selectionRange.startContainer.parentNode as Element;
          const parentEnd = selectionRange.endContainer.parentNode as Element;
          const parentParagraphStart = parentStart.closest('p');
          const parentParagraphEnd = parentEnd.closest('p');

          let selectionMissprint;
          if (
            parentParagraphStart &&
            parentParagraphEnd &&
            parentParagraphStart === parentParagraphEnd
          ) {
            selectionMissprint = parentParagraphStart.textContent?.replace(
              selectionText,
              `*${selectionText}*`,
            );
          } else {
            selectionMissprint = selectionText;
          }

          useModal().open(
            MissPrint,
            {
              closeClickOutside: true,
              selection: selectionText,
              missprint: selectionMissprint,
              link: selectionLink,
            },
            {
              transition: 'modal-fade',
            },
          );
        }
      },

      percentageSendAnalytics(typeCategory: InstanceOptions | undefined, newValue: number) {
        if (!typeCategory) {
          return;
        }

        const { thresholds } = typeCategory;

        const viewedThresholds = Object.keys(thresholds).filter(
          (threshold) => +threshold <= newValue && !thresholds[threshold],
        );

        viewedThresholds.forEach((threshold) => {
          thresholds[threshold] = true;

          const scrollEventParams = {
            content: {
              event: 'скролл материала %',
              value: threshold.toString(),
            },
          };

          window.ym(YA_METRIC_ID, 'params', scrollEventParams);

          useNuxtApp().$sendYandexMetrika({
            level1: 'Скролл материала',
            level4: threshold.toString(),
          });
        });
      },

      onScroll(): void {
        const contentPercent = viewedContentPercent(scrolledElementSelector);
        const contentPercentPromo = viewedContentPercent(scrolledElementSelectorPromo);

        if (contentPercent) {
          this.percentageSendAnalytics(getContentObject(scrolledElementSelector), contentPercent);
        } else if (contentPercentPromo) {
          this.percentageSendAnalytics(
            getContentObject(scrolledElementSelectorPromo),
            contentPercentPromo,
          );
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-page {
    width: 100%;
    display: flex;
    @include tablet {
      background: transparent;
    }
    &__component {
      margin: 0 auto;
      width: 100%;
      // * для брендирования, чтобы баннер не перекрывал контент
      z-index: 2;
    }
  }
</style>
